import type { FC } from 'react';

import { useCms, useImageMapper } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Link } from '@eneco/next';
import { Box, ButtonLink, Card, Heading, Image, Stack, Stretch, Text, TextLink } from '@eneco/sparky';

import type { LineUpItem } from './line-up-content.types';

/**
 * A `LineUp` item as an article card. Mirrors `libs/sitecore/components/content/src/ArticleCard/ArticleCard.tsx`,
 * which the legacy `LineUp` renders for its items. Its heading level is `h3`: `LineUp` hands its items no
 * rendering parameters, on either lane.
 */
export const ArticleCard: FC<{ item: LineUpItem }> = ({ item }) => {
  const mapImage = useImageMapper();
  const imageProps = mapImage(item.image);
  const link = linkWithUrl(item.link);

  return (
    <Card>
      {imageProps?.src && (
        <Box overflow="hidden" borderRadius="m">
          <Image
            alt={imageProps.alt ?? ''}
            width="100%"
            height="auto"
            src={imageProps.src}
            sources={imageProps.sources}
            objectFit="cover"
            aspectRatio="16/9"
          />
        </Box>
      )}
      <Box padding="6">
        <Stack gap="6">
          {item.title && (
            <Heading size="S" as="h3">
              {item.title}
            </Heading>
          )}
          {link?.text && (
            <Link linkType={link.linkType ?? ''} linkValue={{ href: link.href, target: link.target }}>
              <TextLink emphasis="high">{link.text}</TextLink>
            </Link>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

/**
 * A `LineUp` item as a product card. Mirrors `libs/sitecore/components/content/src/ProductCard/ProductCard.tsx`,
 * which the legacy `LineUp` renders for its items.
 */
export const ProductCard: FC<{ item: LineUpItem }> = ({ item }) => {
  const { adapter } = useCms();
  const mapImage = useImageMapper();
  const imageProps = mapImage(item.image);
  const richText = adapter.toRichText(item.content);
  const buttonLink = linkWithUrl(item.buttonLink);
  const link = linkWithUrl(item.link);

  return (
    <Stretch height>
      <Stack>
        {imageProps?.src ? (
          <Stack.Item>
            <Box paddingBottom={{ initial: '6' }}>
              <Image
                src={imageProps.src}
                alt={imageProps.alt ?? ''}
                width="100%"
                height="100%"
                objectFit="cover"
                sources={imageProps.sources}
              />
            </Box>
          </Stack.Item>
        ) : null}
        <Stack.Item>
          <Heading as="h3" size={{ initial: 'XS', md: 'S' }}>
            {item.title}
          </Heading>
        </Stack.Item>
        <Stack.Item grow>
          <Box paddingTop={{ initial: '6' }}>
            <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
              <RichText html={richText} />
            </Text>
          </Box>
        </Stack.Item>

        <Box paddingTop={{ initial: '6' }}>
          <Stretch width={{ md: false, sm: true }}>
            {buttonLink?.text ? (
              <Stretch width={{ initial: true, md: false }}>
                <Link
                  linkType={buttonLink.linkType ?? ''}
                  linkValue={{ href: buttonLink.href, target: buttonLink.target }}>
                  <ButtonLink>{buttonLink.text}</ButtonLink>
                </Link>
              </Stretch>
            ) : null}

            {link?.text ? (
              <Stack.Item>
                <Link linkType={link.linkType ?? ''} linkValue={{ href: link.href, target: link.target }}>
                  <TextLink emphasis="high">{link.text}</TextLink>
                </Link>
              </Stack.Item>
            ) : null}
          </Stretch>
        </Box>
      </Stack>
    </Stretch>
  );
};
